import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/* ── Default feature flags ── */
const DEFAULT_FLAGS = {
  darkMode:           false,  // light theme by default (formal)
  compactSidebar:     false,  // collapse sidebar by default
  showAttendanceAlert:true,   // warn when attendance < 75%
  showCgpaAlert:      true,   // warn when CGPA drops below threshold
  animationsEnabled:  true,   // slide / fade / float animations
  notificationsEnabled:true,  // in-app notification badges
  autoExpandZones:    false,  // auto-open all sidebar zones
  showQuickStats:     true,   // dashboard quick-stat cards
  showNewFeatures:    true,   // "New this month" strip on dashboard
  emailDigest:        false,  // (future) daily summary emails
  whatsappAlerts:     true,   // WhatsApp notification opt-in
};

const STORAGE_KEY = 'aura_settings'; // v2 key — retires g2u_settings so the formal light default applies

function load() {
  try { return { ...DEFAULT_FLAGS, ...JSON.parse(localStorage.getItem(STORAGE_KEY)) }; }
  catch { return DEFAULT_FLAGS; }
}

function save(flags) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
}

/* ── Apply dark class to <html> ── */
function applyDark(enabled) {
  if (enabled) document.documentElement.classList.add('dark');
  else         document.documentElement.classList.remove('dark');
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [flags, setFlags] = useState(() => {
    const f = load();
    applyDark(f.darkMode);
    return f;
  });

  // Keep <html> in sync whenever darkMode changes
  useEffect(() => { applyDark(flags.darkMode); }, [flags.darkMode]);

  // Keep animations class in sync
  useEffect(() => {
    if (!flags.animationsEnabled) document.documentElement.classList.add('no-animations');
    else document.documentElement.classList.remove('no-animations');
  }, [flags.animationsEnabled]);

  const setFlag = useCallback((key, value) => {
    setFlags(prev => {
      const next = { ...prev, [key]: value };
      save(next);
      return next;
    });
  }, []);

  const toggleFlag = useCallback((key) => {
    setFlags(prev => {
      const next = { ...prev, [key]: !prev[key] };
      save(next);
      return next;
    });
  }, []);

  const resetFlags = useCallback(() => {
    save(DEFAULT_FLAGS);
    setFlags(DEFAULT_FLAGS);
  }, []);

  return (
    <ThemeContext.Provider value={{ flags, setFlag, toggleFlag, resetFlags }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
